/**
 * BudgetBar — barra visual de progreso de presupuesto con estado.
 *
 * Estados:
 *   ok          → barra verde, <70%
 *   warning     → barra ámbar, 70–100%
 *   exceeded    → barra roja que rebasa visualmente el contenedor, >100%
 *   unbudgeted  → NO se renderiza barra. Línea discontinua + chip.
 *                 La decisión deliberada: "sin presupuesto" NO es "0%".
 */
import React from "react";

const STATUS_STYLES = {
  ok: {
    bar: "bg-[#6b8e5a]",
    chip: "bg-[#eef4ea] text-[#3d5a30] border-[#c7dcba]",
    label: "Al día",
  },
  warning: {
    bar: "bg-[#d4a34a]",
    chip: "bg-[#fbf3df] text-[#7a5a14] border-[#e9ce84]",
    label: "Atención",
  },
  exceeded: {
    bar: "bg-[#b85c3e]",
    chip: "bg-[#fbe8df] text-[#7a2c11] border-[#e8a685]",
    label: "Excedido",
  },
  unbudgeted: {
    bar: null,
    chip: "bg-stone-100 text-stone-600 border-stone-300",
    label: "Sin presupuesto",
  },
};

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return `${Math.round(n).toLocaleString("es-ES")} €`;
}

export function BudgetStatusChip({ status, testId }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.unbudgeted;
  return (
    <span
      className={`inline-flex items-center text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.chip}`}
      data-testid={testId}
    >
      {s.label}
    </span>
  );
}

export function BudgetBar({ budget, actual, pct, status, testId }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.unbudgeted;

  // UNBUDGETED: línea discontinua gris, sin porcentaje numérico, chip claro.
  // Visualmente distinto de "ok con 0%" (ese SÍ tiene barra verde vacía).
  if (status === "unbudgeted") {
    return (
      <div className="flex items-center gap-3" data-testid={testId}>
        <div
          className="flex-1 h-2 rounded-full border border-dashed border-stone-300 bg-transparent"
          aria-label="Sin presupuesto definido"
        />
        <div className="text-xs text-stone-500 whitespace-nowrap min-w-[140px] text-right">
          {actual > 0 ? `${fmt(actual)} gastados` : "Sin actividad"}
        </div>
      </div>
    );
  }

  // ok / warning / exceeded → barra sólida
  const pctNum = typeof pct === "number" ? pct : 0;
  // Para exceeded, la barra llena 100% y dibujamos un overflow visual a la derecha
  const fillPct = Math.min(pctNum * 100, 100);
  const overflow = pctNum > 1;

  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className="flex-1 relative h-2 rounded-full bg-stone-100 overflow-visible">
        <div
          className={`absolute left-0 top-0 h-2 rounded-full ${s.bar}`}
          style={{ width: `${fillPct}%` }}
        />
        {overflow && (
          <div
            className={`absolute top-0 h-2 rounded-r-full ${s.bar} opacity-70`}
            style={{ left: "100%", width: "8%" }}
            aria-hidden
          />
        )}
      </div>
      <div className="text-xs whitespace-nowrap tabular-nums min-w-[170px] text-right">
        <span className={status === "exceeded" ? "font-semibold text-[#7a2c11]" : (status === "warning" ? "font-semibold text-[#7a5a14]" : "text-stone-600")}>
          {Math.round(pctNum * 100)}%
        </span>
        <span className="text-stone-400 mx-1">·</span>
        <span className="text-stone-700">{fmt(actual)} / {fmt(budget)}</span>
      </div>
    </div>
  );
}

export default BudgetBar;
